import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import "./JobDetails.css";
import { Briefcase, Calendar, MapPin, User, FileText } from "lucide-react";
import {
  getJobDetails,
  getPublishJobsByUID,
  assignJobToEmployee,
  assignJobToConsultant,
} from "../../../services/jobService";
import {
  getEmployeeSuggestions,
  getConsultantSuggestions,
} from "../../../services/metadataService";
import Cookies from "js-cookie";
import { Modal, Button } from "react-bootstrap";
import ConfirmationAlert from "../../AlertComponent/ConfirmationAlert";
import NormalAlert from "../../AlertComponent/NormalAlert";
import DOMPurify from "dompurify";


const JobDetails = () => {
  const { jobUID } = useParams();
  const navigate = useNavigate();
  const [jobDetails, setJobDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [employeeCode, setEmployeeCode] = useState("");
  const [employeeName, setEmployeeName] = useState("");
  const [consultantCode, setConsultantCode] = useState("");
  const [consultantName, setConsultantName] = useState("");
  const [showModal, setShowModal] = useState(false);

  // recruiter suggestion states
  const [recruiterSuggestions, setRecruiterSuggestions] = useState([]);
  const [showRecruiterSuggestions, setShowRecruiterSuggestions] =
    useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // consultant suggestion states
  const [consultantSuggestions, setConsultantSuggestions] = useState([]);
  const [showConsultantSuggestions, setShowConsultantSuggestions] =
    useState(false);
  const [highlightedConsultantIndex, setHighlightedConsultantIndex] =
    useState(-1);

  //Modal for Recruiter and consultant
  const [showRecruiterModal, setShowRecruiterModal] = useState(false);
  const [showConsultantModal, setShowConsultantModal] = useState(false);

  const [confirmAlert, setConfirmAlert] = useState(null);
  const [alert, setAlert] = useState(null);

  const token = Cookies.get("employeeToken");

  const handleBack = () => {
    navigate("/Dashboard/Draft-Jobs");
  };

  const fetchJobDetails = async () => {
    try {
      const response = await getJobDetails(jobUID);
      if (response) {
        setJobDetails({
          ...response.data.jobData[0],
          assignedEmployee: response.data.assignedEmployee || [],
          assignedConsultant: response.data.assignedConsultant || [],
        });
      }
    } catch (error) {
        const safeMessage =
          error?.response?.data?.message ||
          "Something went wrong. Please try again.";

        setAlert({
          type: "fail",
          message: safeMessage,
        });
      }  finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jobUID) fetchJobDetails();
  }, [jobUID]);

  const formatIndianCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN").format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return dateStr.split(" ")[0];
  };

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handlePublishJob = async () => {
    try {
      const response = await getPublishJobsByUID(jobUID);

      if (response?.data?.status === "Success") {
        setAlert({
          type: response.data.status,
          message: response.data.message,
        });
        navigate("/Dashboard/Live-Jobs");
      } else {
        setAlert({
          type: response.data.status,
          message: response.data.message,
        });
      }
    } catch (error) {
      setAlert({ type: "fail", message: error.response.data.message });
    }
  };

  const handleDeleteRecruiter = (assignedUID) => {
    if (!window.confirm("Are you sure you want to remove this recruiter?"))
      return;

    setJobDetails((prev) => ({
      ...prev,
      assignedEmployee: prev.assignedEmployee.filter(
        (emp) => emp.assignedUID !== assignedUID
      ),
    }));
  };

  if (loading) return <div>Loading job details...</div>;
  if (!jobDetails) return <div>No job details found.</div>;

  return (
    <div className="job-details-wrapper mt-5">
      <div
        className="glass-card animated mx-auto"
        style={{ "max-width": "900px" }}
      >
        <h2 className="role-title">{jobDetails.jobTitle || "Untitled Role"}</h2>

        <div className="badge-row">
          {/* <span className="badge blue">{jobDetails.jobTitle}</span> */}
          <span className="badge green">
            Position Type: {jobDetails.positionType}
          </span>
          {/* <span className="badge gray">{jobDetails.jobCategory}</span> */}
          <span className="badge blue">
            No. of Opening: {jobDetails.numberOfOpenings}
          </span>
        </div>

        <div className="section-title">
          <Briefcase className="me-2" /> Role Info
        </div>
        <div className="grid">
          <div className="field">
            <strong>Function:</strong> {jobDetails.function}
          </div>
          <div className="field">
            <strong>Function ID:</strong> {jobDetails.functionalManagerID}
          </div>
          <div className="field">
            <strong>Department:</strong> {jobDetails.department}
          </div>
          <div className="field">
            <strong>Grade:</strong> {jobDetails.grade}
          </div>
        </div>

        <div className="section-title">
          <User className="me-2" /> Managers
        </div>
        <div className="grid">
          <div className="field">
            <strong>Reporting Manager:</strong>{" "}
            {jobDetails.reportingManageraName}
          </div>
          <div className="field">
            <strong>Reporting Manager ID:</strong>{" "}
            {jobDetails.reportingManagerID}
          </div>
          <div className="field">
            <strong>Functional Manager:</strong>{" "}
            {jobDetails.functionalManageraName}
          </div>
          <div className="field">
            <strong>Functional Manager ID:</strong>{" "}
            {jobDetails.functionalManagerID}
          </div>
        </div>

        <div className="section-title">
          <MapPin className="me-2" /> Location
        </div>
        <div className="grid">
          <div className="field">
            <strong>Job Location:</strong> {jobDetails.location}
          </div>
          <div className="field">
            <strong>Zone:</strong> {jobDetails.zone}
          </div>
        </div>

        <div className="section-title">
          <Calendar className="me-2" /> Miscellaneous
        </div>
        <div className="grid">
          <div className="field">
            <strong>Offered Salary:</strong>{" "}
            {formatIndianCurrency(jobDetails.offeredSalary)}
          </div>
          <div className="field">
            <strong>Qualification:</strong> {jobDetails.minimumQualification}
          </div>
          <div className="field">
            <strong>Experience:</strong> {jobDetails.minimumExperience}
          </div>
          <div className="field">
            <strong>Gender Preference:</strong> {jobDetails.genderPreference}
          </div>
          <div className="field">
            <strong>Requisition Date:</strong>{" "}
            {formatDate(jobDetails.requisitionDate)}
          </div>
        </div>

        <div className="section-title">
          <FileText className="me-2" /> Job Description
        </div>
        <div
          className="field mb-4 job-description"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(jobDetails.jobDescription || "No description provided."),
          }}
        ></div>

        <div className="section-title mt-4">Assign Recruiter / Consultant</div>
        <div className="mb-3">
          {/* Recruiter Section */}
          {/* <div className="field w-100">
            <h6 className="text-primary fs-5">Assigned Recruiters:</h6>
            {jobDetails.assignedEmployee?.length > 0 ? (
              <ul className="list-group mb-2">
                {jobDetails.assignedEmployee.map((emp) => (
                  <li
                    key={emp.assignedUID}
                    className="list-group-item d-flex justify-content-between align-items-center"
                  >
                    <span>
                      <strong>{emp.employeeName}</strong> ({emp.employeeCode})
                    </span>
                    <span className="text-muted">{emp.email}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted">No recruiter assigned yet.</p>
            )}

            <button
              type="button"
              className="btn btn-outline-primary mt-2"
              onClick={() => setShowRecruiterModal(true)}
            >
              Assign Recruiter
            </button>
          </div> */}

          <div className="field w-100">
            <h6 className="text-primary fs-5">Assigned Recruiters:</h6>
            {jobDetails.assignedEmployee?.length > 0 ? (
              <ul className="list-group mb-2">
                {jobDetails.assignedEmployee.map((emp) => (
                  <li
                    key={emp.assignedUID}
                    className="list-group-item d-flex justify-content-between align-items-center"
                  >
                    <span>
                      <strong>{emp.employeeName}</strong> ({emp.employeeCode})
                    </span>

                    <span className="d-flex align-items-center gap-3">
                      <span className="text-muted">{emp.email}</span>
                      {/* Trash Icon */}
                      <i
                        className="bi bi-trash text-danger"
                        role="button"
                        title="Remove Recruiter"
                        onClick={() => handleDeleteRecruiter(emp.assignedUID)}
                      ></i>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted">No recruiter assigned yet.</p>
            )}

            <button
              type="button"
              className="btn btn-outline-primary mt-2"
              onClick={() => setShowRecruiterModal(true)}
            >
              Assign Recruiter
            </button>
          </div>

          {/* Consultant Section */}
          <div className="field w-100 mt-5">
            <h6 className="text-primary fs-5">Assigned Consultants:</h6>
            {jobDetails.assignedConsultant?.length > 0 ? (
              <ul className="list-group mb-2">
                {jobDetails.assignedConsultant.map((con) => (
                  <li
                    key={con.assignedUID}
                    className="list-group-item d-flex justify-content-between align-items-center"
                  >
                    <span>
                      <strong>{con.consultantName}</strong> (
                      {con.consultantCode})
                    </span>
                    <span className="text-muted">{con.email}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted">No consultant assigned yet.</p>
            )}

            <button
              type="button"
              className="btn btn-outline-primary mt-2"
              onClick={() => setShowConsultantModal(true)}
            >
              Assign Consultant
            </button>
          </div>
        </div>

        <div className="text-center mt-4">
          {jobDetails.isPublish == "True" ? (
            <>
              <Link
                to={`/Dashboard/send-candidate/${jobUID}`}
                className="btn btn-outline-primary px-5 py-2 mx-3"
                // onClick={handleShowCandidateForm(job.jobUID)}
              >
                Submit Candidate
              </Link>
            </>
          ) : (
            <>
              <button
                className="btn btn-outline-primary px-5 py-2 mx-3"
                onClick={() =>
                  setConfirmAlert({
                    message: "Are you sure you want to publish this job?",
                    onConfirm: handlePublishJob,
                    onCancel: () => setConfirmAlert(null),
                  })
                }
              >
                Publish Job
              </button>
            </>
          )}
        </div>
      </div>

      {/*Recruiter Modal*/}
      <Modal
        show={showRecruiterModal}
        onHide={() => setShowRecruiterModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Assign Recruiter</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3 position-relative">
            <label className="form-label">Recruiter Code / Name</label>
            <input
              type="text"
              className="form-control"
              placeholder="Type recruiter code or name"
              value={employeeCode}
              onChange={async (e) => {
                const value = e.target.value;
                setEmployeeCode(value);

                if (value.trim().length > 1) {
                  try {
                    const res = await getEmployeeSuggestions(value.trim());
                    if (res?.length > 0) {
                      setRecruiterSuggestions(res);
                      setShowRecruiterSuggestions(true);
                      setHighlightedIndex(-1);
                    } else {
                      setRecruiterSuggestions([]);
                      setShowRecruiterSuggestions(false);
                    }
                  } catch (error) {
        const safeMessage =
          error?.response?.data?.message ||
          "Something went wrong. Please try again.";

        setAlert({
          type: "fail",
          message: safeMessage,
        });
      } 
                } else {
                  setRecruiterSuggestions([]);
                  setShowRecruiterSuggestions(false);
                }
              }}
              onKeyDown={(e) => {
                if (
                  !showRecruiterSuggestions ||
                  recruiterSuggestions.length === 0
                )
                  return;
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setHighlightedIndex((prev) =>
                    prev < recruiterSuggestions.length - 1 ? prev + 1 : 0
                  );
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setHighlightedIndex((prev) =>
                    prev > 0 ? prev - 1 : recruiterSuggestions.length - 1
                  );
                } else if (e.key === "Enter" && highlightedIndex >= 0) {
                  e.preventDefault();
                  const s = recruiterSuggestions[highlightedIndex];
                  setEmployeeCode(`${s.employeeCode} - ${s.employeeName}`);
                  setEmployeeName(s.employeeName);
                  setRecruiterSuggestions([]);
                  setShowRecruiterSuggestions(false);
                }
              }}
              onBlur={() =>
                setTimeout(() => setShowRecruiterSuggestions(false), 200)
              }
              autoComplete="off"
            />
            {showRecruiterSuggestions && recruiterSuggestions.length > 0 && (
              <ul
                className="list-group position-absolute w-100"
                style={{ zIndex: 1000 }}
              >
                {recruiterSuggestions.map((s, idx) => (
                  <li
                    key={s.employeeCode}
                    className={`list-group-item list-group-item-action ${
                      highlightedIndex === idx ? "active" : ""
                    }`}
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setEmployeeCode(`${s.employeeCode} - ${s.employeeName}`);
                      setEmployeeName(s.employeeName);
                      setRecruiterSuggestions([]);
                      setShowRecruiterSuggestions(false);
                    }}
                  >
                    {s.employeeCode} - {s.employeeName}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowRecruiterModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!employeeCode || !employeeName}
            onClick={async () => {
              try {
                const res = await assignJobToEmployee(
                  jobUID,
                  employeeCode.split(" - ")[0],
                  token
                );
                if (res.data.status === "Success") {
                  setAlert({
                    type: "success",
                    message:
                      res.data.message || "Recruiter assigned successfully!",
                  });
                  setEmployeeCode("");
                  setEmployeeName("");
                  await fetchJobDetails();
                  setShowRecruiterModal(false);
                } else {
                  setAlert({
                    type: "danger",
                    message: res.data.message || "Failed to assign recruiter.",
                  });
                }
              } catch (err) {
                setAlert({
                  type: "danger",
                  message: "Unable to assign this job to the employee.",
                });
              }
            }}
          >
            Add
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Consultant Modal */}
      <Modal
        show={showConsultantModal}
        onHide={() => setShowConsultantModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Assign Consultant</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3 position-relative">
            <label className="form-label">Consultant Code / Name</label>
            <input
              type="text"
              className="form-control"
              placeholder="Type consultant code or name"
              value={consultantCode}
              onChange={async (e) => {
                const value = e.target.value;
                setConsultantCode(value);

                if (value.trim().length > 1) {
                  try {
                    const res = await getConsultantSuggestions(value.trim());
                    if (res?.length > 0) {
                      setConsultantSuggestions(res);
                      setShowConsultantSuggestions(true);
                      setHighlightedConsultantIndex(-1);
                    } else {
                      setConsultantSuggestions([]);
                      setShowConsultantSuggestions(false);
                    }
                  } catch (error) {
        const safeMessage =
          error?.response?.data?.message ||
          "Something went wrong. Please try again.";

        setAlert({
          type: "fail",
          message: safeMessage,
        });
      } 
                } else {
                  setConsultantSuggestions([]);
                  setShowConsultantSuggestions(false);
                }
              }}
              onKeyDown={(e) => {
                if (
                  !showConsultantSuggestions ||
                  consultantSuggestions.length === 0
                )
                  return;
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setHighlightedConsultantIndex((prev) =>
                    prev < consultantSuggestions.length - 1 ? prev + 1 : 0
                  );
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setHighlightedConsultantIndex((prev) =>
                    prev > 0 ? prev - 1 : consultantSuggestions.length - 1
                  );
                } else if (
                  e.key === "Enter" &&
                  highlightedConsultantIndex >= 0
                ) {
                  e.preventDefault();
                  const s = consultantSuggestions[highlightedConsultantIndex];
                  setConsultantCode(
                    `${s.consultantCode} - ${s.consultantName}`
                  );
                  setConsultantName(s.consultantName);
                  setConsultantSuggestions([]);
                  setShowConsultantSuggestions(false);
                }
              }}
              onBlur={() =>
                setTimeout(() => setShowConsultantSuggestions(false), 200)
              }
              autoComplete="off"
            />
            {showConsultantSuggestions && consultantSuggestions.length > 0 && (
              <ul
                className="list-group position-absolute w-100"
                style={{ zIndex: 1000 }}
              >
                {consultantSuggestions.map((s, idx) => (
                  <li
                    key={s.consultantCode}
                    className={`list-group-item list-group-item-action ${
                      highlightedConsultantIndex === idx ? "active" : ""
                    }`}
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setConsultantCode(
                        `${s.consultantCode} - ${s.consultantName}`
                      );
                      setConsultantName(s.consultantName);
                      setConsultantSuggestions([]);
                      setShowConsultantSuggestions(false);
                    }}
                  >
                    {s.consultantCode} - {s.consultantName}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConsultantModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!consultantCode || !consultantName}
            onClick={async () => {
              try {
                const res = await assignJobToConsultant(
                  jobUID,
                  consultantCode.split(" - ")[0],
                  token
                );
                if (res.data.status === "Success") {
                  setAlert({
                    type: "success",
                    message:
                      res.data.message || "Consultant assigned successfully!",
                  });
                  setConsultantCode("");
                  setConsultantName("");
                  await fetchJobDetails();
                  setShowConsultantModal(false);
                } else {
                  setAlert({
                    type: "danger",
                    message: res.data.message || "Failed to assign Consultant.",
                  });
                }
              } catch (error) {
        const safeMessage =
          error?.response?.data?.message ||
          "Something went wrong. Please try again.";

        setAlert({
          type: "fail",
          message: safeMessage,
        });
      } 
            }}
          >
            Add
          </Button>
        </Modal.Footer>
      </Modal>

      {confirmAlert && (
        <ConfirmationAlert
          message={confirmAlert.message}
          onConfirm={() => {
            setConfirmAlert(null);
            confirmAlert.onConfirm();
          }}
          onCancel={() => setConfirmAlert(null)}
        />
      )}

      {alert && (
        <NormalAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </div>
  );
};

export default JobDetails;
